import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Save } from 'lucide-react';
import {
  compactSelectClass,
  fieldClass,
  labelClass,
  primaryBtnClass,
  secondaryBtnClass,
} from '../styles';
import {
  createProductCategory,
  fetchCategoryTypes,
  fetchProductCategories,
  updateProductCategory,
} from './api';
import BlobPicker from './BlobPicker';
import {
  CategoryTypeRow,
  emptyProductCategoryForm,
  formFromProductCategory,
  MarketplaceMeta,
  ProductCategoryFormValue,
  ProductCategoryRow,
} from './types';

interface Props {
  meta: MarketplaceMeta;
}

const ProductCategoriesPanel: React.FC<Props> = ({ meta }) => {
  const [types, setTypes] = useState<CategoryTypeRow[]>([]);
  const [rows, setRows] = useState<ProductCategoryRow[]>([]);
  const [typeFilter, setTypeFilter] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<ProductCategoryFormValue>(emptyProductCategoryForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [typeRows, categoryRows] = await Promise.all([
        fetchCategoryTypes(),
        fetchProductCategories(),
      ]);
      setTypes(typeRows);
      setRows(categoryRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (selectedId === 'new') {
      setForm(emptyProductCategoryForm(typeFilter || types[0]?.id || ''));
      return;
    }
    if (selectedId == null) return;
    const row = rows.find((r) => r.id === selectedId);
    if (row) setForm(formFromProductCategory(row));
  }, [selectedId, rows, typeFilter, types]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (typeFilter && r.category_type !== typeFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        (r.workspace_key || '').toLowerCase().includes(q) ||
        (r.short_description || '').toLowerCase().includes(q)
      );
    });
  }, [rows, typeFilter, search]);

  const save = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (form.category_type === '') {
      setError('Category type is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (selectedId === 'new') {
        const created = await createProductCategory(form);
        setRows((prev) => [...prev, created]);
        setSelectedId(created.id);
        setMessage('Product category created.');
      } else if (typeof selectedId === 'number') {
        const updated = await updateProductCategory(selectedId, form);
        setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        setForm(formFromProductCategory(updated));
        setMessage('Product category saved.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading product categories…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(error || message) && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || message}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[520px]">
        <div className="lg:col-span-4 border border-[#E8E8E8] rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-[#E8E8E8] space-y-2">
            <div className="flex items-center gap-2">
              <input
                className={`${fieldClass} h-9`}
                placeholder="Search categories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className={`${secondaryBtnClass} shrink-0 inline-flex items-center gap-1`}
                onClick={() => {
                  setSelectedId('new');
                  setMessage(null);
                  setError(null);
                }}
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            </div>
            <select
              className={compactSelectClass}
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value ? Number(e.target.value) : '')
              }
              aria-label="Filter by category type"
            >
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <ul className="flex-1 max-h-[480px] overflow-y-auto">
            {filtered.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(row.id);
                    setMessage(null);
                    setError(null);
                  }}
                  className={`w-full px-3 py-2.5 text-left text-sm border-b border-[#F0F0F0] ${
                    selectedId === row.id
                      ? 'bg-[#F3F8FC] text-[#0C0B0B] font-medium'
                      : 'text-[#4A4A4A] hover:bg-[#FAFAFA]'
                  }`}
                >
                  <div>{row.name}</div>
                  <div className="text-[11px] text-[#8A8A8A] mt-0.5">
                    {row.category_type_name || '—'}
                    {row.workspace_key ? ` · ${row.workspace_key}` : ''}
                    {` · ${row.product_count ?? 0} products`}
                  </div>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-sm text-[#8A8A8A]">No product categories</li>
            )}
          </ul>
        </div>

        <div className="lg:col-span-8 border border-[#E8E8E8] rounded-lg p-4">
          {selectedId == null ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-sm text-[#8A8A8A] border border-dashed border-[#E0E0E0] rounded-lg">
              Select a product category or click Add.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-[#0C0B0B]">
                  {selectedId === 'new' ? 'New product category' : 'Edit product category'}
                </h3>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save()}
                  className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
                >
                  <Save className="w-3.5 h-3.5" />
                  Save
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelClass}>Name</label>
                  <input
                    className={fieldClass}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Category type</label>
                  <select
                    className={fieldClass}
                    value={form.category_type}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        category_type: e.target.value ? Number(e.target.value) : '',
                      })
                    }
                  >
                    <option value="">Select type…</option>
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Workspace key</label>
                  <select
                    className={fieldClass}
                    value={form.workspace_key}
                    onChange={(e) => setForm({ ...form, workspace_key: e.target.value })}
                  >
                    <option value="">(none)</option>
                    {meta.workspace_keys.map((key) => (
                      <option key={key.value} value={key.value}>
                        {key.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Short description</label>
                  <textarea
                    className={`${fieldClass} min-h-[64px]`}
                    value={form.short_description}
                    onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Long description</label>
                  <textarea
                    className={`${fieldClass} min-h-[100px]`}
                    value={form.long_description}
                    onChange={(e) => setForm({ ...form, long_description: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <BlobPicker
                    value={form.image}
                    imageUrl={form.image_url}
                    configured={meta.blob.configured}
                    container={meta.blob.container}
                    onChange={(name, url) =>
                      setForm({
                        ...form,
                        image: name,
                        image_url: url || (name ? form.image_url : ''),
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCategoriesPanel;
